import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, ClipboardList, LineChart, QrCode, FileText, ShieldCheck,
  Languages, MessageSquare, Activity, Dumbbell, Menu, X } from "lucide-react";
import { useI18n, LANGS } from "../i18n";
import { LOGO, Button, cx } from "../components/ui";

const HERO = "https://images.unsplash.com/photo-1706353399656-210cca727a33?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl2 p-6 shadow-soft hover:-translate-y-1 hover:shadow-card transition-all duration-300">
      <div className="h-12 w-12 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4"><Icon size={24} /></div>
      <h3 className="font-head font-semibold text-navy text-lg mb-1.5">{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{text}</p>
    </div>
  );
}

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const [menu, setMenu] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO} alt="SpinaGo" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-head font-extrabold text-navy text-xl leading-none">Spina<span className="text-teal">Go</span></div>
              <div className="text-[10px] text-slate-400 tracking-wide">by SpinaTech</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 ms-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-teal">{t("nav_features")}</a>
            <a href="#pros" className="hover:text-teal">{t("nav_pros")}</a>
            <a href="#patients" className="hover:text-teal">{t("nav_patients")}</a>
            <a href="#journey" className="hover:text-teal">{t("nav_journey")}</a>
          </nav>
          <div className="ms-auto flex items-center gap-2">
            <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="landing-lang"
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <Link to="/connexion" className="hidden sm:block"><Button variant="outline" size="sm" data-testid="nav-login-btn">{t("nav_login")}</Button></Link>
            <button className="md:hidden text-navy" onClick={() => setMenu(!menu)}>{menu ? <X /> : <Menu />}</button>
          </div>
        </div>
        {menu && (
          <div className="md:hidden border-t border-slate-100 px-5 py-3 space-y-2 text-sm">
            <a href="#features" className="block py-1" onClick={() => setMenu(false)}>{t("nav_features")}</a>
            <a href="#pros" className="block py-1" onClick={() => setMenu(false)}>{t("nav_pros")}</a>
            <a href="#patients" className="block py-1" onClick={() => setMenu(false)}>{t("nav_patients")}</a>
            <Link to="/connexion" className="block py-1 text-teal font-semibold">{t("nav_login")}</Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-fade">
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
              <Activity size={14} /> {t("brand_tagline")}
            </div>
            <h1 className="font-head font-extrabold text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("hero_title")}
            </h1>
            <p className="text-lg text-slate-500 mt-5 leading-relaxed max-w-xl">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/connexion"><Button size="lg" data-testid="hero-cta">{t("cta_start")} <ArrowRight size={18} /></Button></Link>
              <a href="#features"><Button size="lg" variant="outline">{t("cta_demo")}</Button></a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><Languages size={14} /> FR · العربية · EN</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Multicabinet sécurisé</span>
              <span className="flex items-center gap-1.5"><QrCode size={14} /> PDF & QR</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative animate-fade">
            <div className="absolute -inset-4 bg-gradient-to-tr from-teal/20 to-navy/10 rounded-[2rem] blur-2xl" />
            <img src={HERO} alt="Professionnel de santé avec un patient" className="relative rounded-[1.5rem] shadow-card w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-5 start-5 bg-white rounded-xl shadow-card p-4 flex items-center gap-3 border border-slate-100">
              <div className="h-10 w-10 rounded-lg bg-teal text-white flex items-center justify-center"><LineChart size={20} /></div>
              <div><div className="text-sm font-bold text-navy font-head">Adhésion suivie</div><div className="text-xs text-slate-400">Douleur · Difficulté · Progrès</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 bg-vlg">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-teal mb-2">{t("features")}</div>
            <h2 className="font-head font-bold text-navy text-3xl sm:text-4xl tracking-tight">Une plateforme complète pour la réadaptation</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon={Stethoscope} title="Dossiers patients" text="Créez et gérez les dossiers, le contexte clinique, les notes et les consentements." />
            <Feature icon={Dumbbell} title="Bibliothèque d'exercices" text="Trois niveaux : global SpinaTech, cabinet et personnel, avec images et vidéos originales." />
            <Feature icon={ClipboardList} title="Programmes personnalisés" text="Prescrivez à partir de modèles, versionnez et adaptez chaque programme au patient." />
            <Feature icon={FileText} title="PDF & Code QR" text="Générez des documents multilingues avec accès sécurisé par code QR." />
            <Feature icon={LineChart} title="Suivi de l'adhésion" text="Recueillez douleur, difficulté et commentaires. Visualisez la progression." />
            <Feature icon={Activity} title="Parcours Hill DT" text="Documentez le parcours de décompression — sans aucune recommandation automatique." />
          </div>
        </div>
      </section>

      {/* Pros / Patients */}
      <section id="pros" className="py-16">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-6">
          <div className="bg-navy text-white rounded-xl2 p-8 lg:p-10">
            <MessageSquare className="text-teal-400 mb-4" size={28} />
            <h3 className="font-head font-bold text-2xl mb-2">Pour les professionnels</h3>
            <p className="text-white/70 leading-relaxed">Prescrivez, expliquez et suivez la progression de vos patients. Restez concentré sur la clinique : SpinaGo ne pose aucun diagnostic et ne recommande aucun paramètre.</p>
          </div>
          <div id="patients" className="bg-teal text-white rounded-xl2 p-8 lg:p-10">
            <QrCode className="text-white/90 mb-4" size={28} />
            <h3 className="font-head font-bold text-2xl mb-2">Pour les patients</h3>
            <p className="text-white/80 leading-relaxed">Votre programme thérapeutique, partout avec vous. Consultez vos exercices, regardez les vidéos et enregistrez votre progression, même à domicile.</p>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section id="journey" className="py-16 bg-vlg">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <h2 className="font-head font-bold text-navy text-3xl tracking-tight mb-3">Parcours SpinaTech — Hill DT</h2>
          <p className="text-slate-500 leading-relaxed">Un module facultatif pour documenter le parcours de décompression. Chaque décision clinique est saisie et approuvée par le professionnel. SpinaGo ne suggère jamais de force, d'angle ni de durée.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="bg-gradient-to-r from-navy to-navy-700 rounded-xl2 p-10 text-center">
            <h2 className="font-head font-bold text-white text-3xl mb-3">Prêt à commencer ?</h2>
            <p className="text-white/70 mb-6">Connectez-vous avec un compte de démonstration pour explorer SpinaGo.</p>
            <Link to="/connexion"><Button size="lg" data-testid="cta-bottom">{t("cta_start")} <ArrowRight size={18} /></Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white/60 py-10">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-col md:flex-row gap-6 justify-between items-start">
            <div>
              <div className="flex items-center gap-2 text-white mb-2">
                <img src={LOGO} className="h-8 w-8 object-contain bg-white rounded-md p-0.5" alt="" />
                <span className="font-head font-bold text-lg">SpinaGo <span className="text-teal-400 text-sm font-normal">by SpinaTech</span></span>
              </div>
              <p className="text-sm max-w-md">{t("brand_tagline")}</p>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/confidentialite" className="hover:text-teal-400">{t("privacy")}</Link>
              <Link to="/conditions" className="hover:text-teal-400">{t("terms")}</Link>
              <Link to="/connexion" className="hover:text-teal-400">{t("nav_login")}</Link>
            </div>
          </div>
          <p className="text-xs text-white/40 mt-8 border-t border-white/10 pt-6 leading-relaxed">
            {t("footer_legal")} SpinaGo n'est pas affilié à Physitrack. © {new Date().getFullYear()} SpinaTech.
          </p>
        </div>
      </footer>
    </div>
  );
}
